import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterState } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { ConfirmationDialogComponent } from 'src/app/dialogs/confirmation-dialog/confirmation-dialog.component';
import { SynchronizeTrademarkCandidatesDialogComponent, SynchronizeTrademarkCandidatesDialogData } from 'src/app/dialogs/synchronize-trademark-candidates-dialog/synchronize-trademark-candidates-dialog.component';
import { synchronizationFailureKey, synchronizedValues } from 'src/app/helpers/trademark-synchronization.helper';
import { Paginator } from 'src/app/models/paginator';
import { ProductService, ProductServiceType } from 'src/app/models/productService';
import { SynchronizationCandidate, Trademark } from 'src/app/models/trademark';
import { AclService } from 'src/app/services/acl.service';
import { MessageService } from 'src/app/services/message.service';
import { TrademarkService } from 'src/app/services/trademark.service';

@Component({
  selector: 'app-trademark',
  templateUrl: './trademark.component.html',
  styleUrls: ['./trademark.component.scss']
})
export class TrademarkComponent implements OnInit {
  mode: "CONSULTATION" | "EDITION" | "COMPARISON" = "CONSULTATION";
  from: string = "TRADEMARKS";

  trademark: Trademark = {
    _id: undefined,
    identifierNumber: '',
    registrationNumber: undefined,
    applicationNumber: undefined,
    countryOfDesignation: '',
    kind: undefined,
    type: undefined,
    niceClassification: [],
    applicationDate: undefined,
    publicationDate: undefined,
    registrationDate: undefined,
    oppositionEndDate: undefined,
    expirationDate: undefined,
    reproductionURI: undefined,
    name: undefined,
    organizationId: undefined,
    clientReference: undefined,
  };
  productsServices: ProductService[] = [];
  refreshed?: SynchronizationCandidate;
  isObligated: boolean = false;

  reproduction?: File;

  loading: boolean = false;

  documents: Paginator<Document> = {
    docs: [],
    page: 1,
    offset: 0,
    limit: 10,
    totalDocs: 0,
    sort: '-createdAt'
  };
  filteredDocuments: Document[] = [];
  displayedColumns: string[] = ['select', 'name', 'trademark', 'country', 'period', 'upload', 'uploader', 'actions'];

  originalNiceClassification: {
    classNumber: number,
    details?: string
  }[] = [];

  constructor(
    private _message: MessageService,
    private aclService: AclService,
    private dialog: MatDialog,
    private location: Location,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private trademarkService: TrademarkService,
    private translate: TranslateService
  ) {
    this.from = window.history.state.from || "TRADEMARKS";
    this.mode = window.history.state.mode || "CONSULTATION";
  }

  async ngOnInit() {
    const params = await firstValueFrom(this.route.params);
    this.retrieveTrademark(params['id']);
  }


  async retrieveTrademark(id: string) {
    try {
      this._message.emitChange("LOADING", "START");
      this.loading = true;
      this.trademark = await this.trademarkService.retrieve(id);
      this.isObligated = (await this.trademarkService.retrieveObligationOfUse(id)).isObligated;
      this.originalNiceClassification = JSON.parse(JSON.stringify(this.trademark.niceClassification));
      await this.retrieveProductsServices(id);
      this.loading = false;
      this._message.emitChange("LOADING", "END");
    } catch (err) {
      if (err instanceof HttpErrorResponse && err.status === 404) {
        this.router.navigate(['errors', '404'])
      }
      this.loading = false;
      this._message.emitChange("LOADING", "END");
      this.toastr.error('ERRORS.GENERIC');
    }
  }

  async retrieveProductsServices(id: string) {
    try {
      const semiColons = /[\u037e|\u061b|\u1364|\u204f|\u236e|\u2e35|\ua6f6|\ufe14|\uff1b|\u003b]/u
      this._message.emitChange("LOADING", "START");
      this.loading = true;
      this.productsServices = await this.trademarkService.retrieveProductsAndServices(id);
      if (this.productsServices.length === 0 && this.trademark.niceClassification) {
        this.trademark.niceClassification.forEach((nice) => {
          if (nice.details) {
            nice.details.split(semiColons).forEach((term) => {
              if (this.trademark._id) {
                this.productsServices.push({
                  label: term.trim(),
                  trademark: {
                    ref: this.trademark._id,
                    identifierNumber: this.trademark.identifierNumber,
                    countryOfDesignation: this.trademark.countryOfDesignation,
                    name: this.trademark.name
                  },
                  organizationId: this.trademark.organizationId,
                  classNumber: nice.classNumber,
                  type: nice.classNumber >= 35 ? ProductServiceType.SERVICES : ProductServiceType.PRODUCTS,
                  lang: this.trademark.applicationLanguageCode,
                  translations: {}
                })
              }

            })
          }

        })
      }
      this._message.emitChange("LOADING", "END");
    } catch (err) {
      this.loading = false;
      this._message.emitChange("LOADING", "END");
      this.toastr.error('ERRORS.GENERIC');
    }
  }



  _hasTrademark() {
    return this.trademark?.identifierNumber && this.trademark.identifierNumber !== '' && this.trademark.countryOfDesignation && this.trademark.countryOfDesignation.length > 0;
  }

  async refresh() {
    try {
      if (this._hasTrademark() && this.trademark._id) {

        this.loading = true;
        this._message.emitChange("LOADING", "START");
        const { failure, candidates } = await this.trademarkService.synchronizationCandidates(this.trademark._id);

        if (!candidates || candidates.length === 0) {
          this.toastr.error(synchronizationFailureKey(failure));
        } else if (candidates.length === 1 && candidates[0].match) {
          this.refreshed = candidates[0];
          this.mode = "COMPARISON";
        } else {
          const data: SynchronizeTrademarkCandidatesDialogData = {
            candidates,
            failure,
            source: this.trademark
          };
          const config: MatDialogConfig = {
            panelClass: 'dialog-container',
            width: '900px',
            maxHeight: '90vh',
            data
          }
          const dialog = this.dialog.open(SynchronizeTrademarkCandidatesDialogComponent, config);
          const result: { selected: boolean, trademark?: SynchronizationCandidate } = await firstValueFrom(dialog.afterClosed());
          if (result && result.selected && result.trademark) {
            this.refreshed = result.trademark;
            this.mode = "COMPARISON";
          }
        }

        this.loading = false;
        this._message.emitChange("LOADING", "END");
      }
    } catch (err: any) {
      console.log(err)
      if (err && err.error && err.error.message && err.error.message === "EXTERNAL_TRADEMARK_NOT_FOUND") {
        this.toastr.error(`ERRORS.${err.error.message}`);
      } else {
        this.toastr.error(`ERRORS.GENERIC`);
      }
      this.loading = false;
      this._message.emitChange("LOADING", "END");

    }
  }

  async update() {
    try {
      if (this._hasTrademark()) {
        this._message.emitChange("LOADING", "START");
        this.loading = true;


        if (this.mode === 'COMPARISON' && this.refreshed) {
          // The same values as the comparison shows: the identification number and the territory never change
          Object.assign(this.trademark, synchronizedValues(this.trademark, this.refreshed));
        }
        if (this.mode === 'EDITION' && this.reproduction && this.trademark) {
          const res = await this.trademarkService.uploadReproduction(this.reproduction);
          this.trademark.reproductionURI = res.url;
        }
        delete this.trademark.embeddings;
        await this.confirmUpdateIfNeeded(this.mode === 'COMPARISON' && !!this.refreshed);

      } else {
        console.error("Can't update without trademark")
      }
    } catch (err) {
      console.error(err);
      this.loading = false;
      this._message.emitChange("LOADING", "END");
      this.toastr.error('ERRORS.GENERIC');
    }
  }

  async confirmUpdateIfNeeded(synchronized: boolean = false) {
    const originalTerms: string[] = [];
    const newTerms: string[] = [];
    this.originalNiceClassification.forEach((n) => {
      const terms = n.details?.split(';');
      terms?.forEach((t) => originalTerms.push(t.trim()))
    })
    this.trademark.niceClassification?.forEach((n) => {
      const terms = n.details?.split(';');
      terms?.forEach((t) => newTerms.push(t.trim()))
    })
    if (!_.isEqual(originalTerms, newTerms)) {
      const config: MatDialogConfig = {
        panelClass: 'dialog-container',
        width: '480px',
        data: {
          title: 'TRADEMARK.UPDATE_TRADEMARK_TERMS_TITLE',
          text: this.translate.instant('TRADEMARK.UPDATE_TRADEMARK_TERMS_TEXT', {
            name: this.trademark.name ?? '',
            identifierNumber: this.trademark.identifierNumber
          }),
          button: {
            text: 'ACTIONS.UPDATE',
            class: 'main-button'
          }
        }
      }
      const dialog = this.dialog.open(ConfirmationDialogComponent, config);
      const result: { confirmed: boolean } = await firstValueFrom(dialog.afterClosed());
      if (result && result.confirmed && this.trademark) {
        await this.saveTrademarkUpdate(synchronized)
      } else {
        this.loading = false;
        this._message.emitChange("LOADING", "END");
      }
    } else {
      await this.saveTrademarkUpdate(synchronized)
    }
  }


  async delete() {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        title: 'TRADEMARK.DELETE_TRADEMARK_TITLE',
        text: this.translate.instant('TRADEMARK.DELETE_TRADEMARK_TEXT', { name: this.trademark.name ?? '', identifierNumber: this.trademark.identifierNumber }),
        button: {
          text: 'ACTIONS.DELETE',
          class: 'danger-button'
        },
        confirmation: {
          text: 'TRADEMARK.DELETE_TRADEMARK_CONFIRMATION',
          value: this.trademark?.identifierNumber
        }
      }
    }
    const dialog = this.dialog.open(ConfirmationDialogComponent, config);
    const result: { confirmed: boolean } = await firstValueFrom(dialog.afterClosed());
    if (result && result.confirmed && this.trademark) {
      try {
        this.loading = true;
        this._message.emitChange("LOADING", "START");
        this.trademarkService.delete(this.trademark);
        this.loading = false;
        this._message.emitChange("LOADING", "END");
        this.location.back();
        this.toastr.success(this.translate.instant('TRADEMARK.DELETE_TRADEMARK_SUCCESS', { name: this.trademark.name ?? '', identifierNumber: this.trademark.identifierNumber }));
      } catch (err) {
        console.error(err);
        this.loading = false;
        this._message.emitChange("LOADING", "END");
        this.toastr.error('ERRORS.GENERIC');
      }
    }
  }

  onReproductionChange(reproduction: any) {
    this.reproduction = reproduction;
    if (!this.reproduction && this.trademark) {
      this.trademark.reproductionURI = undefined;
    }
  }

  private async saveTrademarkUpdate(synchronized: boolean = false) {
    try {
      this._message.emitChange("LOADING", "START");
      this.loading = true;
      await this.trademarkService.update(this.trademark, synchronized);
      this.loading = false;
      this._message.emitChange("LOADING", "END");
      this.toastr.success(this.translate.instant('TRADEMARK.UPDATE_SUCCESS', { name: this.trademark.name || this.trademark.identifierNumber }));
      this.mode = 'CONSULTATION';
      if (this.trademark && this.trademark._id) {
        await this.retrieveTrademark(this.trademark._id)
      }
    } catch (err) {
      this.loading = false;
      this._message.emitChange("LOADING", "END");
      this.toastr.error('ERRORS.GENERIC');
    }
  }


}
